import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Server, Wifi, Code, 
  CheckCircle2, Zap, Clock, Users, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { 
  TiltCard, FloatingElement, GlowOrb, ParticleField, 
  MorphingShape, Grid3D, GlowingCard, BeamEffect,
  ScrollReveal3D, ScrollParallaxSection, FloatingOrb3D
} from '../components/3DEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { login } = useAuth();

  useEffect(() => {
    fetch(`${API}/seed`, { method: 'POST' }).catch(() => {});
  }, []);

  const services = [
    {
      icon: Server,
      title: 'IT Infrastructure',
      description: 'Complete hardware maintenance, diagnostics, and 24/7 support for your critical systems.',
      image: 'https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?w=600&q=80'
    },
    {
      icon: Wifi,
      title: 'Network Solutions',
      description: 'Enterprise-grade networking, firewall setup, VPN configuration, and bandwidth optimization.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80'
    },
    {
      icon: Shield,
      title: 'Security Systems',
      description: 'HD surveillance, 24/7 monitoring, cloud backup, and intelligent motion detection.',
      image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=600&q=80'
    },
    {
      icon: Code,
      title: 'Software Solutions',
      description: 'Custom development, ERP integration, database management, and dedicated support.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&q=80'
    }
  ];

  const stats = [
    { value: '500+', label: 'Businesses Served', icon: Users },
    { value: '99.9%', label: 'Uptime Guaranteed', icon: Zap },
    { value: '24/7', label: 'Support Available', icon: Clock },
    { value: '15+', label: 'Years Experience', icon: Award },
  ];

  const features = [
    'Proactive system monitoring',
    'Rapid response SLA',
    'Certified technicians',
    'Transparent pricing',
    'Flexible contracts',
    'Dedicated account manager'
  ];

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#080B14' }}>
      {/* Hero Section with 3D Effects */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <Grid3D />
          <ParticleField count={35} />
          
          {/* Glow Orbs */}
          <GlowOrb color="royal" size={600} className="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
          <GlowOrb color="midnight" size={500} className="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />
          
          {/* Morphing Shape */}
          <MorphingShape className="top-1/4 right-1/4" />
        </div>

        {/* Hero Background Image with Parallax */}
        <motion.div 
          className="absolute inset-0 z-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <img 
            src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=80"
            alt="Server Infrastructure"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080B14]/90 via-[#080B14]/75 to-[#080B14]" />
        </motion.div>

        {/* Animated Beams */}
        <BeamEffect className="top-1/3 left-0" />
        <BeamEffect className="top-2/3 left-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-8 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                  <Zap className="w-4 h-4 text-blue-400 fill-blue-400/20" />
                  Enterprise-Grade AMC Solutions
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                data-testid="hero-title"
              >
                <span className="text-white">Transform Your</span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent">
                  IT Infrastructure
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-gray-400 mb-10 max-w-xl"
              >
                Comprehensive annual maintenance contracts for IT hardware, networking, 
                CCTV, and software solutions. Trusted by 500+ businesses worldwide.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Button 
                  onClick={login}
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold px-8 py-6 text-base rounded-full hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] transition-all duration-300 border-none shadow-[0_4px_15px_rgba(59,130,246,0.2)]"
                  data-testid="hero-cta"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/pricing">
                  <Button 
                    variant="outline"
                    className="border-blue-500/20 text-white bg-transparent px-8 py-6 text-base rounded-full hover:border-blue-400 hover:text-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)] transition-all duration-300"
                    data-testid="hero-pricing"
                  >
                    View Pricing
                  </Button>
                </Link>
              </motion.div>
            </div>

            {/* Right Side - 3D Elements */}
            <div className="hidden lg:block relative">
              <ScrollReveal3D>
                {/* Main Image with Floating Animation */}
                <div className="relative">
                  <FloatingElement duration={8} yOffset={15}>
                    <div className="relative">
                      <img 
                        src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80"
                        alt="Server Infrastructure"
                        className="w-full h-auto rounded-2xl shadow-2xl border border-blue-500/10"
                        style={{ 
                          filter: 'drop-shadow(0 0 60px rgba(59, 130, 246, 0.2))'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#080B14] via-transparent to-transparent rounded-2xl" />
                    </div>
                  </FloatingElement>
                </div>

                {/* Floating Stats Card */}
                <FloatingElement duration={6} yOffset={10} delay={1} className="absolute -bottom-6 -left-6">
                  <GlowingCard disableHover={true} className="rounded-xl p-6 glass-premium border-blue-500/20 shadow-2xl">
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent mb-1">99.9%</div>
                    <div className="text-sm text-gray-400">Uptime Guarantee</div>
                  </GlowingCard>
                </FloatingElement>

                {/* Floating Badge */}
                <FloatingElement duration={7} yOffset={12} delay={0.5} className="absolute top-10 -left-10">
                  <div className="bg-gradient-to-r from-blue-500/15 to-cyan-400/15 backdrop-blur-xl border border-blue-500/20 rounded-xl p-4 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">Enterprise Security</span>
                    </div>
                  </div>
                </FloatingElement>
              </ScrollReveal3D>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative border-t border-b border-blue-500/10">
        <div className="absolute inset-0">
          <GlowOrb color="blue" size={300} className="top-1/2 left-1/4 -translate-y-1/2" blur={80} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <TiltCard intensity={8} className="h-full">
                    <div className="text-center p-6 rounded-2xl glass-premium neo-card h-full transition-colors border border-blue-500/5">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-400/5 flex items-center justify-center mx-auto mb-4 border border-blue-500/10 shadow-[0_0_15px_rgba(59,130,246,0.15)]">
                        <Icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </div>
                  </TiltCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section with 3D Cards */}
      <section className="py-24 relative" id="services">
        <div className="absolute inset-0">
          <MorphingShape className="top-0 left-1/4" />
          <GlowOrb color="royal" size={450} className="bottom-0 right-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase mb-4 block">Our Services</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Comprehensive AMC Solutions
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From IT hardware to advanced security systems, we provide end-to-end maintenance 
              and support for all your technology infrastructure needs.
            </p>
          </motion.div>

          <ScrollReveal3D className="grid md:grid-cols-2 gap-6">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <TiltCard intensity={5} className="h-full">
                    <GlowingCard
                      className="group relative overflow-hidden rounded-2xl h-full border border-blue-500/10 shadow-lg shadow-black/50"
                      data-testid={`service-card-${idx}`}
                    >
                      <div className="absolute inset-0">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className="w-full h-full object-cover opacity-15 group-hover:opacity-25 group-hover:scale-110 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1224] via-[#0D1224]/90 to-transparent" />
                      </div>
                      <div className="relative p-8">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-400/5 flex items-center justify-center mb-6 group-hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] border border-blue-500/10 transition-all duration-300">
                          <Icon className="w-7 h-7 text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                        <p className="text-gray-400 leading-relaxed">{service.description}</p>
                        <Link 
                          to="/services" 
                          className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium mt-6 group-hover:gap-3 transition-all"
                        >
                          Learn More <ArrowRight className="w-4 h-4 text-blue-400" />
                        </Link>
                      </div>
                    </GlowingCard>
                  </TiltCard>
                </motion.div>
              );
            })}
          </ScrollReveal3D>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative overflow-hidden border-t border-blue-500/10">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80"
            alt="Network Background"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080B14] via-[#080B14]/95 to-[#080B14]" />
          <ParticleField count={20} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-blue-400 text-sm font-medium tracking-wider uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Enterprise-Grade Support, <br />Startup-Friendly Service
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                We combine the reliability of enterprise solutions with the agility and 
                personalized service that growing businesses need. Our team is committed 
                to your success.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, idx) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-lg glass-premium border border-blue-500/10 hover:border-blue-500/30 transition-colors shadow-sm"
                  >
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold px-8 py-6 rounded-full hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] border-none shadow-[0_4px_15px_rgba(59,130,246,0.2)] transition-shadow">
                    Schedule a Consultation
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <TiltCard intensity={8}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/80" style={{ border: '1px solid rgba(99, 130, 255, 0.15)' }}>
                  <img 
                    src="https://images.pexels.com/photos/8463151/pexels-photo-8463151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Professional Team"
                    className="w-full aspect-[4/3] object-cover opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080B14] via-transparent to-transparent" />
                </div>
              </TiltCard>

              {/* Floating Elements */}
              <FloatingElement duration={6} yOffset={15} className="absolute -bottom-6 -left-6">
                <GlowingCard disableHover={true} className="rounded-xl p-6 glass-premium border-blue-500/20 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/10">
                      <CheckCircle2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">24/7</div>
                      <div className="text-sm text-gray-400">Support</div>
                    </div>
                  </div>
                </GlowingCard>
              </FloatingElement>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative border-t border-blue-500/10">
        <div className="absolute inset-0">
          <GlowOrb color="blue" size={500} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <TiltCard intensity={3}>
              <div className="glass-premium border border-blue-500/15 rounded-3xl p-12 shadow-2xl">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Ready to Transform Your IT Operations?
                </h2>
                <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                  Join 500+ businesses that trust NetEye for their technology 
                  infrastructure needs. Get started with a free consultation.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button 
                    onClick={login}
                    className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-90 text-white font-semibold px-8 py-6 text-base rounded-full hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] border-none shadow-[0_4px_15px_rgba(59,130,246,0.2)]"
                    data-testid="cta-get-started"
                  >
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Link to="/contact">
                    <Button 
                      variant="outline"
                      className="border-blue-500/20 text-white bg-transparent px-8 py-6 text-base rounded-full hover:border-blue-400 hover:text-blue-400 hover:bg-blue-500/5 transition-colors duration-300"
                    >
                      Contact Sales
                    </Button>
                  </Link>
                </div>
              </div>
            </TiltCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
